import { apiClient } from './apiClient'
import { buildQueryString } from '../utils/pagination'
import type { Operator, PaginatedResponse } from '@/types'

export const operatorService = {
  // Get all operators with pagination and filters
  async getOperators(params?: {
    page?: number
    limit?: number
    search?: string
    country_id?: number
    active?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
  }): Promise<PaginatedResponse<Operator>> {
    const queryString = buildQueryString('/operators', params || {})
    const url = queryString ? `/operators?${queryString}` : '/operators'
    const response = await apiClient.get(url)    
    return response.data
  },

  // Get all operators (for dropdowns)
  async getAllOperators(): Promise<Operator[]> {
    const response = await apiClient.get('/operators/all')
    return response.data.data
  },

  // Get active operators
  async getActiveOperators(): Promise<Operator[]> {
    const response = await apiClient.get('/operators/active')
    return response.data.data
  },

  // Get operators by country
  async getByCountry(countryId: number, params?: {
    page?: number
    limit?: number
    active?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
  }): Promise<PaginatedResponse<Operator>> {
    const queryString = buildQueryString('/operators', params || {})
    const url = queryString ? `/operators/country/${countryId}?${queryString}` : `/operators/country/${countryId}`
    const response = await apiClient.get(url)
    return response.data
  },

  // Get operators by status
  async getByStatus(active: number, params?: {
    page?: number
    limit?: number
    country_id?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
  }): Promise<PaginatedResponse<Operator>> {
    const queryString = buildQueryString('/operators', params || {})
    const url = queryString ? `/operators/status/${active}?${queryString}` : `/operators/status/${active}`
    const response = await apiClient.get(url)
    return response.data
  },

  // Get operators with impact limits
  async getWithImpactLimits(params?: {
    page?: number
    limit?: number
    country_id?: number
    active?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
  }): Promise<PaginatedResponse<Operator>> {
    const response = await apiClient.get('/operators/with-impact-limits', { params })
    return response.data
  },

  // Get operator by ID
  async getById(id: number): Promise<Operator> {
    const response = await apiClient.get(`/operators/${id}`)
    return response.data.data
  },

  // Create new operator
  async createOperator(data: Partial<Operator>): Promise<Operator> {
    const response = await apiClient.post('/operators', data)
    return response.data.data
  },

  // Update operator
  async updateOperator(id: number, data: Partial<Operator>): Promise<Operator> {
    const response = await apiClient.put(`/operators/${id}`, data)
    return response.data.data
  },

  // Delete operator
  async deleteOperator(id: number): Promise<void> {
    await apiClient.delete(`/operators/${id}`)
  },

  // Search operators
  async search(query: string): Promise<Operator[]> {
    const response = await apiClient.get('/operators', {
      params: { search: query, limit: 10 }
    })
    return response.data.data
  },

  // Alias methods for backward compatibility
  async getAll(params?: any) {
    return this.getOperators(params)
  },

  async create(data: Partial<Operator>) {
    return this.createOperator(data)
  },

  async update(id: number, data: Partial<Operator>) {
    return this.updateOperator(id, data)
  },

  async delete(id: number) {
    return this.deleteOperator(id)
  }
}
